import React, { useState, useRef, useEffect, useContext } from "react";
import WindowHeader from "./Layout/WindowHeader/WindowHeader";
import headeLogo from "../assets/images/shersoftnavLogo.png";
import { AuthContext } from "../context/AuthContext";
import { useDbContext } from "../context/DbContext";
import axios from "axios";
import { useSelector } from "react-redux";
import { selectActiveLocation } from "../app/locationSlice";
import {
  FiMessageSquare,
  FiCheckCircle,
  FiRefreshCw,
  FiSave,
  FiX,
  FiMapPin,
  FiTag,
  FiSend,
  FiAlignLeft,
  FiSmartphone,
  FiShield,
  FiPlus,
} from "react-icons/fi";
import { FaCheckDouble } from "react-icons/fa";

// Available dynamic variable tags palette with color tokens
const VARIABLES = [
  {
    key: "accountNo",
    label: "A/C No",
    badgeBg: "bg-blue-100 text-blue-700 border-blue-200",
    btnBg: "bg-blue-50 hover:bg-blue-100 border-blue-200 text-blue-800",
  },
  {
    key: "customerName",
    label: "Name",
    badgeBg: "bg-indigo-100 text-indigo-700 border-indigo-200",
    btnBg: "bg-indigo-50 hover:bg-indigo-100 border-indigo-200 text-indigo-800",
  },
  {
    key: "total",
    label: "Amount",
    badgeBg: "bg-amber-100 text-amber-800 border-amber-300",
    btnBg: "bg-amber-50 hover:bg-amber-100 border-amber-300 text-amber-900",
  },
  {
    key: "grandTotal",
    label: "Grand Total",
    badgeBg: "bg-emerald-100 text-emerald-700 border-emerald-200",
    btnBg: "bg-emerald-50 hover:bg-emerald-100 border-emerald-200 text-emerald-800",
  },
  {
    key: "gm",
    label: "Gold Grams",
    badgeBg: "bg-yellow-100 text-yellow-800 border-yellow-300",
    btnBg: "bg-yellow-50 hover:bg-yellow-100 border-yellow-300 text-yellow-900",
  },
  {
    key: "netwt",
    label: "Net Weight",
    badgeBg: "bg-purple-100 text-purple-700 border-purple-200",
    btnBg: "bg-purple-50 hover:bg-purple-100 border-purple-200 text-purple-800",
  },
  {
    key: "mobileNo",
    label: "Mobile",
    badgeBg: "bg-cyan-100 text-cyan-700 border-cyan-200",
    btnBg: "bg-cyan-50 hover:bg-cyan-100 border-cyan-200 text-cyan-800",
  },
  {
    key: "showroomName",
    label: "Showroom",
    badgeBg: "bg-slate-200 text-slate-700 border-slate-300",
    btnBg: "bg-slate-100 hover:bg-slate-200 border-slate-300 text-slate-800",
  },
  {
    key: "date",
    label: "Date",
    badgeBg: "bg-slate-200 text-slate-700 border-slate-300",
    btnBg: "bg-slate-100 hover:bg-slate-200 border-slate-300 text-slate-800",
  },
];

// Sample live mock data for real-time mobile simulator rendering
const SAMPLE_MOCK = {
  accountNo: "SW-10492",
  customerName: "Ramesh Kumar",
  total: "5,000",
  grandTotal: "60,000",
  gm: "6.240",
  netwt: "0.500",
  mobileNo: "+91 9800000000",
  showroomName: "SherGold Jewellers",
  date: "01/Oct/2026",
};

// Parse real DLT IDs and Header directly from telecom API link
const parseApiCredentials = (apiUrl) => {
  if (!apiUrl || typeof apiUrl !== "string") {
    return {
      senderId: "",
      templateId: "",
      entityId: "",
      username: "",
      gatewayHost: "",
    };
  }

  let cleanUrl = apiUrl.trim();
  const httpIndex = cleanUrl.search(/https?:\/\//i);
  if (httpIndex > 0) {
    cleanUrl = cleanUrl.substring(httpIndex);
  }

  let host = "";
  try {
    const urlObj = new URL(cleanUrl.startsWith("http") ? cleanUrl : `http://${cleanUrl}`);
    host = urlObj.hostname;
  } catch (e) {
    const m = cleanUrl.match(/https?:\/\/([^/?#]+)/i);
    if (m) host = m[1];
  }

  const getParam = (keys) => {
    for (const k of keys) {
      const regex = new RegExp(`[?&]${k}=([^&#\\s]+)`, "i");
      const match = cleanUrl.match(regex);
      if (match && match[1]) {
        try {
          return decodeURIComponent(match[1]);
        } catch (_) {
          return match[1];
        }
      }
    }
    return "";
  };

  const senderId = getParam([
    "sendername",
    "sender_name",
    "senderid",
    "sender_id",
    "sender",
    "header",
    "from",
    "sid",
  ]);

  const templateId = getParam([
    "tid",
    "templateid",
    "template_id",
    "dlt_template_id",
    "tempid",
    "dlt_te_id",
    "te_id",
  ]);

  const entityId = getParam([
    "peid",
    "pe_id",
    "entityid",
    "entity_id",
    "dlt_entity_id",
    "principal_entity_id",
  ]);

  const username = getParam([
    "username",
    "user",
    "userid",
    "user_id",
    "accname",
  ]);

  return {
    senderId,
    templateId,
    entityId,
    username,
    gatewayHost: host,
  };
};

// Update a query parameter in an API link while preserving all other parameters
const updateApiQueryParam = (apiUrl, keys, newValue) => {
  if (!apiUrl || typeof apiUrl !== "string") return apiUrl;
  for (const k of keys) {
    const regex = new RegExp(`([?&])${k}=[^&#\\s]*`, "i");
    if (regex.test(apiUrl)) {
      return apiUrl.replace(regex, `$1${k}=${encodeURIComponent(newValue)}`);
    }
  }
  return apiUrl;
};

function SmsSettings({ onClose, zIndex }) {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const { dbCode } = useDbContext();
  const activeLocation = useSelector(selectActiveLocation);
  const { agentCode } = useContext(AuthContext);

  // Registered templates list from database table
  const [registeredTemplates, setRegisteredTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [selectedVoucher, setSelectedVoucher] = useState("");

  const [autoSmsActive, setAutoSmsActive] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [testMobile, setTestMobile] = useState("");
  const [testSentStatus, setTestSentStatus] = useState("");

  const [smssettingsData, setsmssettingsData] = useState({
    EntryName: "",
    Template: "",
    api: "",
    dltTemplateId: "",
    senderId: "",
  });

  const textareaRef = useRef(null);

  // Fetch only actual registered SMS templates from the database
  const fetchRegisteredTemplates = async (selectVoucherName = null) => {
    if (!dbCode) return;
    try {
      setLoadingTemplates(true);
      const res = await axios.get(`${apiBaseUrl}/main/selectAllSmsSettings/${dbCode}`);
      const list = Array.isArray(res.data) ? res.data : [];
      setRegisteredTemplates(list);

      if (list.length > 0) {
        // If a specific voucher is requested (e.g. after save), select it; otherwise select first
        const target = selectVoucherName
          ? list.find((t) => t.Voucher === selectVoucherName) || list[0]
          : selectedVoucher
          ? list.find((t) => t.Voucher === selectedVoucher) || list[0]
          : list[0];

        setSelectedVoucher(target.Voucher);
        const parsed = parseApiCredentials(target.ApiLink || "");
        setsmssettingsData({
          EntryName: target.Voucher,
          Template: target.MessageBody || "",
          api: target.ApiLink || "",
          dltTemplateId: parsed.templateId || "",
          senderId: parsed.senderId || "",
        });
      } else {
        // No registered templates in table yet
        setSelectedVoucher("__NEW__");
        setsmssettingsData({
          EntryName: "SCHEME RECEIPT",
          Template: "",
          api: "",
          dltTemplateId: "",
          senderId: "",
        });
      }
    } catch (err) {
      console.error("Error fetching registered templates:", err);
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchRegisteredTemplates();
  }, [dbCode, activeLocation]);

  // Click on a registered template in the left rail
  const handleSelectTemplate = (item) => {
    setSelectedVoucher(item.Voucher);
    const parsed = parseApiCredentials(item.ApiLink || "");
    setsmssettingsData({
      EntryName: item.Voucher,
      Template: item.MessageBody || "",
      api: item.ApiLink || "",
      dltTemplateId: parsed.templateId || "",
      senderId: parsed.senderId || "",
    });
  };

  // Click "+ Create New Template Type"
  const handleCreateNew = () => {
    setSelectedVoucher("__NEW__");
    const parsed = parseApiCredentials(smssettingsData.api || "");
    setsmssettingsData({
      EntryName: "",
      Template: "",
      api: smssettingsData.api || "",
      dltTemplateId: parsed.templateId || "",
      senderId: parsed.senderId || "",
    });
    setTimeout(() => {
      document.getElementById("entry-name-input")?.focus();
    }, 50);
  };

  // Refresh current template from database
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchRegisteredTemplates(selectedVoucher !== "__NEW__" ? selectedVoucher : null);
    setIsRefreshing(false);
  };

  // Drag and Drop handlers for variable chips
  const handleDragStart = (e, variable) => {
    e.dataTransfer.setData("text/plain", `\${${variable}}`);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const variable = e.dataTransfer.getData("text/plain");
    insertVariableText(variable);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // Insert variable text directly at cursor or append
  const insertVariableText = (variableTag) => {
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      const startPos = textarea.selectionStart ?? textarea.value.length;
      const endPos = textarea.selectionEnd ?? textarea.value.length;
      const text = textarea.value;

      const newText =
        text.substring(0, startPos) + variableTag + text.substring(endPos);

      setsmssettingsData((prev) => ({
        ...prev,
        Template: newText,
      }));

      setTimeout(() => {
        textarea.focus();
        textarea.selectionStart = textarea.selectionEnd =
          startPos + variableTag.length;
      }, 0);
    } else {
      setsmssettingsData((prev) => ({
        ...prev,
        Template: (prev.Template ? prev.Template + " " : "") + variableTag,
      }));
    }
  };

  // Clean / Format whitespace in template
  const handleFormatTemplate = () => {
    if (!smssettingsData.Template) return;
    const formatted = smssettingsData.Template.replace(/\s+/g, " ").trim();
    setsmssettingsData((prev) => ({ ...prev, Template: formatted }));
  };

  // Save Settings to database
  const handleSave = async () => {
    const voucherName = smssettingsData.EntryName?.trim();
    if (!voucherName) {
      alert("Please enter a Voucher / Scheme Entry Name.");
      return;
    }
    if (!smssettingsData.Template?.trim()) {
      alert("Please enter the Template message body.");
      return;
    }
    if (!smssettingsData.api?.trim()) {
      alert("Please enter the API link.");
      return;
    }

    try {
      setIsSaving(true);
      const payload = {
        EntryName: voucherName,
        Template: smssettingsData.Template,
        api: smssettingsData.api,
        Location: activeLocation ? activeLocation.id : null,
      };

      const response = await axios.post(
        `${apiBaseUrl}/main/savesmssettings/${dbCode}`,
        payload
      );

      if (response.status === 200 || response.data?.success) {
        alert(`SMS Template for "${voucherName}" saved successfully!`);
        // Refresh registered list so the newly saved voucher appears in the Left Rail
        await fetchRegisteredTemplates(voucherName);
      } else {
        alert("Failed to save settings: " + (response.data?.message || "Unknown error"));
      }
    } catch (error) {
      console.error("Error saving SMS settings:", error);
      alert("Error saving SMS settings: " + (error.response?.data?.message || error.message));
    } finally {
      setIsSaving(false);
    }
  };

  // Handle instant test dispatch (real SMS test to verify gateway and template)
  const handleSendTestSms = async () => {
    const rawNumber = testMobile?.replace(/[^\d+]/g, "")?.trim();
    if (!rawNumber || rawNumber.length < 10) {
      alert("Please enter a valid 10-digit recipient mobile number.");
      return;
    }
    if (!smssettingsData.api?.trim()) {
      alert("Please enter the API Link in the configuration before sending a test SMS.");
      return;
    }

    try {
      setTestSentStatus("Dispatching test SMS via gateway...");

      // Prepare rendered message body using mock data
      let message = smssettingsData.Template || "Test SMS from SherGold ERP";
      Object.entries(SAMPLE_MOCK).forEach(([k, v]) => {
        message = message.replace(new RegExp(`\\$\\{${k}\\}`, "g"), v);
      });

      // Use clean digits as entered by user (supports both 10-digit and country-code formatted numbers)
      const pureDigits = rawNumber.replace(/\D/g, "");
      const formattedMobile = pureDigits;

      // Prepare target URL replacing ${mobileNo}, ${encodeURIComponent(message)}, ${message}
      let url = smssettingsData.api
        .replace(/\$\{mobileNo\}/g, formattedMobile)
        .replace(/\$\{encodeURIComponent\(message\)\}/g, encodeURIComponent(message))
        .replace(/\$\{message\}/g, encodeURIComponent(message));

      // Attempt sending via backend proxy to bypass browser CORS restrictions
      let res;
      try {
        res = await axios.post(`${apiBaseUrl}/main/testsmssend`, { url });
      } catch (proxyErr) {
        // Fallback to direct GET if backend proxy is unreachable
        res = await axios.get(url);
      }

      if (res?.data?.success !== false) {
        const responseText = typeof res?.data === "object" ? JSON.stringify(res.data) : String(res?.data || "OK");
        setTestSentStatus(`Sent test to ${formattedMobile}! Response: ${responseText.substring(0, 75)}`);
        alert(`Test SMS Dispatched!\nRecipient: ${formattedMobile}\nGateway Response: ${responseText.substring(0, 150)}`);
      } else {
        setTestSentStatus(`Gateway Error: ${res?.data?.message || "Failed to deliver"}`);
        alert(`SMS Gateway Error: ${res?.data?.message || "Failed to deliver"}`);
      }
    } catch (error) {
      console.error("Error sending test SMS:", error);
      const msg = error.response?.data?.message || error.message;
      setTestSentStatus(`Dispatch Failed: ${msg}`);
      alert(`Test SMS Dispatch Failed:\n${msg}`);
    } finally {
      setTimeout(() => setTestSentStatus(""), 8000);
    }
  };

  // Keyboard shortcuts (F2 to save, Esc to exit)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "F2") {
        e.preventDefault();
        handleSave();
      } else if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [smssettingsData]);

  // Live credentials extracted dynamically from current API link
  const currentCredentials = parseApiCredentials(smssettingsData.api);
  const activeSenderId = currentCredentials.senderId || smssettingsData.senderId || "";
  const activeTemplateId = currentCredentials.templateId || smssettingsData.dltTemplateId || "";
  const activeEntityId = currentCredentials.entityId || "";
  const activeHost = currentCredentials.gatewayHost || "";
  const activeUser = currentCredentials.username || "";

  // Compute live character count and credits
  const charLength = smssettingsData.Template ? smssettingsData.Template.length : 0;
  const creditsUsed = Math.max(1, Math.ceil(charLength / 160));

  // Resolved dynamic preview message
  const getRenderedPreview = () => {
    if (!smssettingsData.Template) {
      return "No template text configured yet. Select a registered template or type a new message in the editor.";
    }
    let res = smssettingsData.Template;
    Object.entries(SAMPLE_MOCK).forEach(([k, v]) => {
      res = res.replace(new RegExp(`\\$\\{${k}\\}`, "g"), v);
    });
    return res;
  };

  return (
    <div
      className="sms-settings-root absolute inset-0 w-full h-full bg-[#f0f0f0] flex items-center justify-center pointer-events-auto select-none font-['Inter',sans-serif] [&_*]:!font-['Inter',sans-serif]"
      style={{ zIndex: zIndex || 101 }}
    >
      <style>{`
        .sms-settings-root, .sms-settings-root * {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
        }
      `}</style>
      {/* ── Main Modal Container (Exact layout from SoftWareSettings) ── */}
      <div className="bg-white font-normal w-[98%] max-w-[1380px] h-[95%] max-h-[760px] border border-slate-200 shadow-md flex flex-col overflow-hidden absolute">
        {/* ── 1. Window Header (Same as SoftWareSettings) ── */}
        <WindowHeader
          title="SMS Settings Master"
          logo={headeLogo}
          onClose={onClose}
          className="bg-[#0f2d59] text-white h-9 px-3 shrink-0"
          closeBtnClassName="h-6 w-6 my-auto mr-1 bg-red-600 hover:bg-red-700 rounded-sm border-none flex items-center justify-center text-white"
        />

        {/* ── 2. Compact Action Header ── */}
        <section className="bg-white px-3 py-1.5 border-b border-slate-200 flex flex-wrap items-center justify-between gap-2 shadow-[0_1px_2px_rgba(0,0,0,0.02)] shrink-0">
          {/* Title & DLT Badge */}
          <div className="flex items-center space-x-2.5">
            <div className="w-8 h-8 bg-[#0d2347] rounded-lg flex items-center justify-center text-white shrink-0 shadow-xs">
              <FiMessageSquare className="w-4 h-4 text-amber-400" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h1 className="text-sm font-bold text-slate-900 tracking-tight leading-none">
                  SMS &amp; WhatsApp Gateway Manager
                </h1>
                <span className="text-[9px] font-bold bg-blue-50 text-blue-700 border border-blue-200 px-1.5 py-0.2 rounded tracking-wide">
                  SHERGOLD ERP
                </span>
              </div>
              <p className="text-[11px] text-slate-500 leading-tight mt-0.5">
                Automated transactional messaging, customer receipts, and gateway endpoints.
              </p>
            </div>
          </div>

          {/* Right Quick Controls */}
          <div className="flex flex-wrap items-center gap-1.5 text-xs">
            {/* Showroom location card */}
            <div className="flex items-center h-7 bg-slate-50 border border-slate-300 rounded-md px-2 text-slate-700 shadow-xs">
              <FiMapPin className="text-amber-500 mr-1 text-[11px] shrink-0" />
              <span className="text-slate-500 text-[9px] font-semibold mr-1">SHOWROOM:</span>
              <span className="font-bold text-slate-800 text-[10px]">
                {activeLocation?.LocationName || activeLocation?.name || "SHOP (Loc #1)"}
              </span>
            </div>

            {/* Gateway status badge */}
            <div className="flex items-center h-7 space-x-1.5 px-2 bg-emerald-50 border border-emerald-300 rounded-md text-emerald-700 font-bold tracking-wide text-[10px] shadow-xs">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span>GATEWAY ACTIVE</span>
            </div>

            {/* Auto SMS Toggle */}
            <label className="flex items-center h-7 space-x-1.5 bg-slate-50 border border-slate-300 rounded-md px-2 cursor-pointer hover:border-slate-400 transition shadow-xs">
              <span className="text-slate-700 text-[11px] font-medium">Auto SMS</span>
              <button
                type="button"
                onClick={() => setAutoSmsActive(!autoSmsActive)}
                className={`w-7 h-3.5 rounded-full relative p-0.5 transition-colors duration-200 focus:outline-none ${
                  autoSmsActive ? "bg-emerald-500" : "bg-slate-300"
                }`}
                role="switch"
                aria-checked={autoSmsActive}
              >
                <span
                  className={`block w-2.5 h-2.5 bg-white rounded-full shadow transform transition-transform duration-200 ${
                    autoSmsActive ? "translate-x-3.5" : "translate-x-0"
                  }`}
                />
              </button>
            </label>

            {/* Refresh Action */}
            <button
              type="button"
              onClick={handleRefresh}
              className="flex items-center h-7 space-x-1 px-2.5 bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 rounded-md transition font-medium shadow-xs cursor-pointer text-xs"
            >
              <FiRefreshCw className={`w-3 h-3 text-slate-500 ${isRefreshing ? "animate-spin" : ""}`} />
              <span>Refresh</span>
            </button>

            {/* Save Action */}
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center h-7 space-x-1.5 px-3 bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-600 hover:to-amber-500 text-slate-950 font-bold rounded-md shadow-xs transition border border-amber-500 cursor-pointer disabled:opacity-50 text-xs"
            >
              <FiSave className="w-3 h-3" />
              <span>{isSaving ? "Saving..." : "Save Settings"}</span>
            </button>
          </div>
        </section>

        {/* ── 3. Three-Column Workspace Studio ── */}
        <section className="grid grid-cols-12 gap-2.5 p-2.5 bg-slate-50 overflow-y-auto flex-1 min-h-0 font-['Inter',sans-serif]">
          {/* ── COLUMN 1: Only Registered Templates from DB (3 cols) ── */}
          <div className="col-span-12 lg:col-span-3 flex flex-col space-y-2">
            <div className="bg-white border border-slate-200 rounded-lg p-2.5 flex-1 flex flex-col justify-between shadow-xs">
              <div>
                {/* Header & Count */}
                <div className="flex items-center justify-between pb-1.5 mb-1.5 border-b border-slate-100">
                  <div className="flex items-center gap-1.5 text-slate-700">
                    <FiAlignLeft className="w-3.5 h-3.5 text-blue-600" />
                    <span className="text-[10px] font-bold text-slate-700 tracking-wider uppercase">
                      Registered Templates
                    </span>
                  </div>
                  <span className="text-blue-800 bg-blue-100 px-1.5 py-0.2 rounded-full border border-blue-200 text-[9px] font-bold">
                    {registeredTemplates.length} Saved
                  </span>
                </div>

                {/* + New Template Action Button */}
                <button
                  type="button"
                  onClick={handleCreateNew}
                  className={`w-full mb-1.5 h-7 px-2 rounded-md border text-xs font-bold transition flex items-center justify-center space-x-1 cursor-pointer shadow-xs ${
                    selectedVoucher === "__NEW__"
                      ? "bg-blue-600 text-white border-blue-600 shadow-xs"
                      : "bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
                  }`}
                >
                  <FiPlus className="w-3 h-3" />
                  <span>Create New Template Type</span>
                </button>

                {/* Vertical Registered Templates List (Always reserves space for 4 items, then scrolls) */}
                <div className="space-y-1 h-[190px] min-h-[190px] max-h-[190px] overflow-y-auto pr-0.5">
                  {loadingTemplates ? (
                    <div className="h-full flex items-center justify-center p-3 text-center text-xs text-slate-400">
                      Loading registered templates...
                    </div>
                  ) : registeredTemplates.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center p-3 text-center text-xs text-slate-400 bg-slate-50 rounded-md border border-dashed border-slate-200 space-y-0.5">
                      <p className="font-semibold text-slate-600 text-[11px]">No templates registered yet</p>
                      <p className="text-[9px] text-slate-400">
                        Click "Create New Template Type" to configure your first template.
                      </p>
                    </div>
                  ) : (
                    registeredTemplates.map((item) => {
                      const isSelected = selectedVoucher === item.Voucher;
                      return (
                        <button
                          key={item.Voucher}
                          type="button"
                          onClick={() => handleSelectTemplate(item)}
                          className={`w-full h-[44px] shrink-0 text-left px-2 py-1 rounded-md border transition group flex items-center justify-between cursor-pointer ${
                            isSelected
                              ? "bg-amber-50/90 border border-amber-400 text-slate-900 shadow-xs"
                              : "bg-slate-50 hover:bg-slate-100 border-slate-200 text-slate-700 hover:text-slate-900"
                          }`}
                        >
                          <div className="flex items-center space-x-2 min-w-0 flex-1">
                            <span
                              className={`w-2 h-2 rounded-full shrink-0 ${
                                isSelected
                                  ? "bg-emerald-500 ring-2 ring-emerald-300"
                                  : "bg-emerald-500"
                              }`}
                            />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1">
                                <span
                                  className={`text-[11px] truncate leading-tight ${
                                    isSelected
                                      ? "font-bold text-amber-950"
                                      : "font-semibold text-slate-800"
                                  }`}
                                >
                                  {item.Voucher}
                                </span>
                                {item.ApiLink && parseApiCredentials(item.ApiLink).senderId && (
                                  <span className="text-[8px] bg-amber-50 text-amber-900 border border-amber-200 px-1 py-0.2 rounded font-bold shrink-0">
                                    {parseApiCredentials(item.ApiLink).senderId}
                                  </span>
                                )}
                              </div>
                              <div className="text-[9px] text-slate-400 truncate max-w-[170px] leading-tight">
                                {item.MessageBody
                                  ? item.MessageBody
                                  : "No message text configured"}
                              </div>
                            </div>
                          </div>
                          <span
                            className={`text-xs font-bold shrink-0 ml-1 ${
                              isSelected
                                ? "text-amber-600 group-hover:translate-x-0.5 transition-transform"
                                : "text-slate-400 group-hover:text-slate-600"
                            }`}
                          >
                            ›
                          </span>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>

              {/* DLT Telecom Credentials Section (Extracted from API) */}
              <div className="bg-gradient-to-b from-slate-50 to-amber-50/20 border border-slate-200 rounded-lg p-2 text-xs mt-2 space-y-1.5 shadow-xs shrink-0">
                <div className="flex items-center justify-between pb-1 border-b border-slate-200">
                  <div className="flex items-center space-x-1.5">
                    <FiShield className="w-3.5 h-3.5 text-amber-600" />
                    <span className="text-[10px] font-bold text-slate-800 uppercase tracking-wider">
                      DLT Credentials
                    </span>
                  </div>
                  <span className="text-[8px] bg-emerald-50 text-emerald-700 border border-emerald-300 px-1 py-0.2 rounded font-bold">
                    From API
                  </span>
                </div>

                <div className="space-y-1 text-[10px]">
                  {/* Header / Sender Name */}
                  <div className="flex items-center justify-between bg-white px-2 py-1 rounded border border-slate-200">
                    <span className="text-slate-500 font-semibold">Header (Sender Name):</span>
                    <span className="font-bold text-amber-800 bg-amber-50 border border-amber-200 px-1.5 py-0.2 rounded tracking-wider">
                      {activeSenderId || "—"}
                    </span>
                  </div>

                  {/* DLT Template ID (tid) */}
                  <div className="flex items-center justify-between bg-white px-2 py-1 rounded border border-slate-200">
                    <span className="text-slate-500 font-semibold">Template ID (tid):</span>
                    <span className="font-bold text-slate-800 tracking-tight select-all">
                      {activeTemplateId || "—"}
                    </span>
                  </div>

                  {/* Entity ID (peid) */}
                  <div className="flex items-center justify-between bg-white px-2 py-1 rounded border border-slate-200">
                    <span className="text-slate-500 font-semibold">Entity ID (peid):</span>
                    <span className="font-bold text-slate-700 tracking-tight">
                      {activeEntityId || "Registered (DLT)"}
                    </span>
                  </div>

                  {/* Gateway Host */}
                  <div className="flex items-center justify-between bg-white px-2 py-1 rounded border border-slate-200">
                    <span className="text-slate-500 font-semibold">Gateway Host:</span>
                    <span className="font-semibold text-slate-700 truncate max-w-[130px]" title={activeHost}>
                      {activeHost || "—"}
                    </span>
                  </div>

                  {/* Gateway User */}
                  {activeUser && (
                    <div className="flex items-center justify-between bg-white px-2 py-1 rounded border border-slate-200">
                      <span className="text-slate-500 font-semibold">Account User:</span>
                      <span className="font-semibold text-slate-700 truncate max-w-[130px]">
                        {activeUser}
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between pt-0.5 text-[9px] text-slate-500">
                  <span>Table: <strong className="text-slate-700">dbo.SmsSettings</strong></span>
                  <span className="text-emerald-700 font-semibold">✓ DLT Active</span>
                </div>
              </div>
            </div>
          </div>

          {/* ── COLUMN 2: Message Template Studio & Dynamic Tag Palette (5 cols) ── */}
          <div className="col-span-12 lg:col-span-5 flex flex-col space-y-2">
            {/* Telecom Header Card */}
            <div className="bg-white border border-slate-200 rounded-lg p-2.5 space-y-2 shadow-xs">
              <div className="flex items-center justify-between pb-1.5 border-b border-slate-200">
                <div className="flex items-center space-x-1.5">
                  <span className="w-2 h-2 rounded-full bg-amber-500" />
                  <span className="text-[11px] font-bold text-slate-900 uppercase tracking-wider">
                    {smssettingsData.EntryName || "New Template"} Configuration
                  </span>
                </div>
                <span className="text-[9px] bg-emerald-50 text-emerald-700 border border-emerald-300 px-1.5 py-0.2 rounded-full flex items-center space-x-1 font-semibold">
                  <FiCheckCircle className="w-2.5 h-2.5 text-emerald-600" />
                  <span>DLT Compliant</span>
                </span>
              </div>

              {/* Configuration Inputs Grid */}
              <div className="grid grid-cols-12 gap-2 text-xs">
                {/* Entry Type */}
                <div className="col-span-5">
                  <label className="block text-[9px] font-bold text-slate-600 mb-0.5">
                    ENTRY / SCHEME TYPE *
                  </label>
                  <div className="relative">
                    <input
                      id="entry-name-input"
                      type="text"
                      list="voucher-type-suggestions"
                      value={smssettingsData.EntryName}
                      onChange={(e) => {
                        const val = e.target.value.toUpperCase();
                        setsmssettingsData((prev) => ({
                          ...prev,
                          EntryName: val,
                        }));
                      }}
                      placeholder="e.g. SCHEME RECEIPT"
                      className="w-full h-7 bg-slate-50 border border-slate-300 rounded-md px-2 text-xs text-slate-800 font-bold uppercase focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 shadow-xs"
                    />
                    <datalist id="voucher-type-suggestions">
                      <option value="SCHEME RECEIPT" />
                      <option value="RECEIPT" />
                      <option value="SALES" />
                      <option value="ESTIMATE" />
                      <option value="OLD GOLD" />
                      <option value="DUE REMINDER" />
                      {registeredTemplates.map((t) => (
                        <option key={t.Voucher} value={t.Voucher} />
                      ))}
                    </datalist>
                  </div>
                </div>

                {/* DLT Template ID (tid) */}
                <div className="col-span-4">
                  <label className="block text-[9px] font-bold text-slate-600 mb-0.5">
                    DLT TEMPLATE ID (TID)
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={smssettingsData.dltTemplateId || activeTemplateId || ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setsmssettingsData((prev) => {
                          const updatedApi = updateApiQueryParam(prev.api, ["tid", "templateid", "template_id"], val);
                          return {
                            ...prev,
                            dltTemplateId: val,
                            api: updatedApi,
                          };
                        });
                      }}
                      placeholder="e.g. 1707172801856260928"
                      className="w-full h-7 bg-slate-50 border border-slate-300 rounded-md pl-2 pr-6 text-xs font-semibold text-slate-800 focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 shadow-xs"
                    />
                    <span className="absolute right-2 top-1.5 text-emerald-600 font-bold text-xs" title="DLT template ID">
                      ✓
                    </span>
                  </div>
                </div>

                {/* Header / Sender Name */}
                <div className="col-span-3">
                  <label className="block text-[9px] font-bold text-slate-600 mb-0.5">
                    HEADER (SENDER NAME)
                  </label>
                  <div className="flex h-7 shadow-xs rounded-md overflow-hidden">
                    <input
                      type="text"
                      maxLength={6}
                      value={smssettingsData.senderId || activeSenderId || ""}
                      onChange={(e) => {
                        const val = e.target.value.toUpperCase();
                        setsmssettingsData((prev) => {
                          const updatedApi = updateApiQueryParam(prev.api, ["sendername", "sender_name", "senderid", "sender_id", "sender", "sid"], val);
                          return {
                            ...prev,
                            senderId: val,
                            api: updatedApi,
                          };
                        });
                      }}
                      placeholder="e.g. NIVGOD"
                      className="w-full bg-slate-50 border border-slate-300 border-r-0 rounded-l-md px-1.5 text-xs font-bold text-amber-700 tracking-wider text-center focus:bg-white focus:border-blue-500"
                    />
                    <span className="bg-slate-100 border border-slate-300 rounded-r-md px-1 text-[8px] text-slate-600 flex items-center font-semibold">
                      GSM-7
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Template Content Textarea Studio */}
            <div className="bg-white border border-slate-200 rounded-lg p-2.5 space-y-1.5 flex-1 flex flex-col justify-between shadow-xs">
              <div>
                <div className="flex items-center justify-between text-xs mb-1">
                  <div className="flex items-center space-x-1">
                    <span className="font-bold text-slate-800 text-[11px]">Content as in Template</span>
                    <span className="text-rose-500 font-bold text-xs">*</span>
                    <span className="text-[9px] text-slate-500">
                      (exact match with telecom portal)
                    </span>
                  </div>
                  {/* Real-time metrics counters */}
                  <div className="flex items-center space-x-1.5 text-[9px]">
                    <span className="text-slate-600">
                      Length:{" "}
                      <strong className="text-amber-700 font-bold text-xs">
                        {charLength}
                      </strong>{" "}
                      / 160 Chars
                    </span>
                    <span className="bg-blue-100 text-blue-800 border border-blue-200 px-1 py-0.2 rounded font-bold">
                      {creditsUsed} SMS Credit{creditsUsed > 1 ? "s" : ""}
                    </span>
                  </div>
                </div>

                {/* Message Textarea */}
                <div className="relative">
                  <textarea
                    ref={textareaRef}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    rows={3}
                    value={smssettingsData.Template}
                    onChange={(e) =>
                      setsmssettingsData((prev) => ({
                        ...prev,
                        Template: e.target.value,
                      }))
                    }
                    placeholder="Enter template message text (e.g. Dear ${customerName}, received Rs.${total} towards Scheme Ac No: ${accountNo}...) or click variable chips below..."
                    className="w-full bg-slate-50 border border-slate-300 rounded-md p-2 text-xs text-slate-800 leading-snug focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 selection:bg-amber-300 selection:text-slate-900 shadow-inner"
                  />
                  <button
                    type="button"
                    onClick={handleFormatTemplate}
                    className="absolute bottom-2 right-2 bg-white hover:bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded text-[9px] border border-slate-300 flex items-center space-x-1 shadow-xs font-semibold transition cursor-pointer"
                    title="Clean extra spaces and format"
                  >
                    <FiAlignLeft className="w-2.5 h-2.5 text-slate-500" />
                    <span>Format</span>
                  </button>
                </div>
              </div>

              {/* Dynamic Variable Palette with pastel tags */}
              <div className="pt-1.5 border-t border-slate-200 space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-[10px] font-bold text-slate-700 flex items-center gap-1">
                    <FiTag className="w-3 h-3 text-amber-500" />
                    Available Dynamic Variable Tags
                  </span>
                  <span className="text-[9px] text-slate-500">
                    Click chip to append or drag into editor
                  </span>
                </div>

                <div className="flex flex-wrap gap-1">
                  {VARIABLES.map((v) => (
                    <button
                      key={v.key}
                      type="button"
                      draggable
                      onDragStart={(e) => handleDragStart(e, v.key)}
                      onClick={() => insertVariableText(`\${${v.key}}`)}
                      className={`flex items-center space-x-1 border rounded px-1.5 py-0.5 text-[10px] transition shadow-xs font-medium cursor-pointer ${v.btnBg}`}
                    >
                      <span className="font-bold">+</span>
                      <span>{`\${${v.key}}`}</span>
                      <span
                        className={`text-[8px] border px-0.5 rounded ml-0.5 ${v.badgeBg}`}
                      >
                        {v.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* HTTP Gateway REST Endpoint Strip */}
              <div className="mt-1 pt-1.5 border-t border-slate-200">
                <div className="flex items-center justify-between text-[10px] mb-0.5">
                  <span className="font-bold text-slate-700 flex items-center space-x-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />
                    <span>HTTP REST Gateway Endpoint Configuration</span>
                  </span>
                  <span className="text-[9px] text-slate-500 font-semibold">
                    Protocol: HTTPS GET / POST
                  </span>
                </div>
                <div className="flex h-7 shadow-xs rounded-md overflow-hidden">
                  <span className="bg-blue-50 border border-r-0 border-slate-300 text-blue-700 px-2 text-[10px] rounded-l-md font-bold flex items-center">
                    GET
                  </span>
                  <input
                    type="text"
                    value={smssettingsData.api}
                    onChange={(e) => {
                      const newApi = e.target.value;
                      const parsed = parseApiCredentials(newApi);
                      setsmssettingsData((prev) => ({
                        ...prev,
                        api: newApi,
                        dltTemplateId: parsed.templateId || prev.dltTemplateId,
                        senderId: parsed.senderId || prev.senderId,
                      }));
                    }}
                    placeholder="https://sapteleservices.com/SMS_API/sendsms.php?username=...&sendername=NIVGOD&tid=1707..."
                    className="w-full bg-slate-50 border border-slate-300 px-2 py-0.5 text-[10px] text-slate-700 focus:bg-white focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (!smssettingsData.api) {
                        alert("API Link is currently empty.");
                      } else {
                        const parsed = parseApiCredentials(smssettingsData.api);
                        alert(
                          `DLT API Verified!\nGateway Host: ${parsed.gatewayHost || "—"}\nHeader (Sender Name): ${parsed.senderId || "None detected"}\nTemplate ID (tid): ${parsed.templateId || "None detected"}\nAccount User: ${parsed.username || "—"}`
                        );
                      }
                    }}
                    className="bg-slate-100 hover:bg-slate-200 border border-l-0 border-slate-300 text-slate-700 px-2.5 text-xs rounded-r-md font-semibold transition cursor-pointer"
                  >
                    Check
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* ── COLUMN 3: Customer Mobile Device Preview & Test Dispatch (4 cols) ── */}
          <div className="col-span-12 lg:col-span-4 flex flex-col space-y-2">
            {/* Device Section Header */}
            <div className="flex items-center justify-between px-1">
              <div className="flex items-center space-x-1.5 text-xs font-bold text-slate-800">
                <FiSmartphone className="w-3.5 h-3.5 text-amber-500" />
                <span className="text-[11px]">Customer Mobile Preview</span>
              </div>
              <span className="text-[9px] bg-amber-100 text-amber-900 border border-amber-300 px-1.5 py-0.2 rounded font-semibold">
                Live Simulation
              </span>
            </div>

            {/* Phone Frame Container (Sleek Compact Display Mockup) */}
            <div className="bg-[#0f172a] border-2 border-slate-800 rounded-2xl p-2 shadow-lg flex-1 flex flex-col justify-between max-w-[280px] mx-auto w-full">
              {/* Dynamic Island / Speaker notch */}
              <div className="flex justify-between items-center px-2 py-0.5 text-[9px] text-slate-400">
                <span>11:54</span>
                <div className="w-12 h-2.5 bg-black rounded-full border border-slate-800" />
                <div className="flex items-center space-x-1">
                  <span>5G</span>
                  <span>98%</span>
                </div>
              </div>

              {/* In-Phone SMS App Header */}
              <div className="bg-[#1e293b] rounded-lg p-1.5 my-1 flex items-center space-x-2 border border-slate-700/60 shadow-xs">
                <div className="w-6 h-6 rounded-full bg-gradient-to-tr from-amber-500 to-yellow-500 text-slate-950 font-black text-[10px] flex items-center justify-center shadow-xs">
                  {activeSenderId ? activeSenderId.substring(0, 2).toUpperCase() : "SG"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-1">
                    <span className="font-bold text-[11px] text-white truncate">
                      {activeSenderId ? `VM-${activeSenderId}` : "VM-SHERGOLD"}
                    </span>
                    <FiCheckCircle className="w-3 h-3 text-blue-400 shrink-0" />
                  </div>
                  <div className="text-[8px] text-emerald-400 font-medium leading-none">
                    Verified Business SMS
                  </div>
                </div>
              </div>

              {/* Chat Bubble Area (Resolved with Live Dynamic Mock Values) */}
              <div className="flex-1 py-1.5 px-0.5 flex flex-col justify-end space-y-1">
                <div className="text-center">
                  <span className="text-[8px] text-slate-400 bg-slate-800/80 px-1.5 py-0.2 rounded-full font-semibold">
                    TODAY 11:54 AM
                  </span>
                </div>

                {/* Message Balloon */}
                <div className="bg-[#1e293b] border border-slate-700/80 text-slate-200 rounded-xl rounded-tl-xs p-2 text-xs shadow-sm space-y-1 leading-snug">
                  {/* Jeweller Stamp */}
                  <div className="flex justify-between items-center text-[9px] text-amber-400 font-bold border-b border-slate-700/80 pb-0.5">
                    <span>SHERGOLD JEWELLERS</span>
                    <span className="text-slate-400 text-[8px] font-semibold">REC# 8842</span>
                  </div>

                  {/* Dynamic Rendered Message Body */}
                  <p className="text-[10px] text-slate-200 break-words whitespace-pre-wrap line-clamp-4 leading-tight">
                    {getRenderedPreview()}
                  </p>

                  {/* Summary Metric Breakdown Card */}
                  <div className="text-[9px] bg-[#0f172a] p-1.5 rounded border border-slate-800 space-y-0.5">
                    <div className="flex justify-between text-slate-300">
                      <span>Accrued:</span>
                      <span className="font-bold text-amber-400">6.240g</span>
                    </div>
                    <div className="flex justify-between text-slate-300 pt-0.5 border-t border-slate-800">
                      <span>Grand Total:</span>
                      <span className="font-bold text-emerald-400">Rs.60,000</span>
                    </div>
                  </div>

                  {/* Delivery Tick & Meta */}
                  <div className="text-[8px] text-slate-400 pt-0.5 flex justify-between items-center">
                    <span>SherGold ERP</span>
                    <span className="text-emerald-400 flex items-center gap-1 font-semibold">
                      <span>Delivered</span>
                      <FaCheckDouble className="w-2 h-2 text-emerald-400" />
                    </span>
                  </div>
                </div>
              </div>

              {/* Home indicator bar */}
              <div className="w-16 h-0.5 bg-slate-700 rounded-full mx-auto mt-1" />
            </div>

            {/* Instant Test Console (Light Container Under Phone) */}
            <div className="bg-white border border-slate-200 rounded-lg p-2 space-y-1.5 shadow-xs">
              <div className="flex items-center justify-between text-xs">
                <span className="font-bold text-slate-800 text-[10px] flex items-center space-x-1">
                  <FiSend className="w-3 h-3 text-blue-600" />
                  <span>Test Dispatch Console</span>
                </span>
                <span className="text-[8px] bg-emerald-100 text-emerald-800 border border-emerald-300 px-1 py-0.2 rounded font-bold">
                  HTTP 200 OK
                </span>
              </div>
              <div className="flex space-x-1">
                <input
                  type="text"
                  value={testMobile}
                  onChange={(e) => setTestMobile(e.target.value)}
                  placeholder="Enter receiver mobile (e.g. 9876543210)"
                  className="flex-1 h-7 bg-slate-50 border border-slate-300 rounded-md px-2 text-xs text-slate-800 focus:bg-white focus:border-blue-500 font-medium shadow-xs"
                />
                <button
                  type="button"
                  onClick={handleSendTestSms}
                  className="h-7 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-2.5 rounded-md text-xs flex items-center space-x-1 shadow-xs transition cursor-pointer"
                >
                  <FiSend className="w-3 h-3" />
                  <span>Send Test</span>
                </button>
              </div>
              {testSentStatus ? (
                <p className="text-[9px] text-emerald-600 font-semibold animate-pulse leading-none">
                  {testSentStatus}
                </p>
              ) : (
                <p className="text-[9px] text-slate-500 leading-none">
                  Dispatches live HTTP request using current gateway endpoint.
                </p>
              )}
            </div>
          </div>
        </section>

        {/* ── 4. Docked Modal Footer ── */}
        <footer className="bg-white border-t border-slate-200/80 px-3 py-1.5 flex flex-wrap items-center justify-between gap-1.5 text-xs shrink-0 select-none">
          {/* Real Status Info */}
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-1.5 text-slate-700 text-[11px]">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span className="font-medium text-slate-600">Company DB:</span>
              <strong className="text-slate-900 bg-slate-100 px-1.5 py-0.2 rounded border border-slate-200 text-[11px]">
                {dbCode}
              </strong>
              <span className="text-slate-300">|</span>
              <span className="text-slate-600 font-medium">Templates:</span>
              <span className="text-blue-800 font-bold bg-blue-50 px-1.5 py-0.2 rounded border border-blue-200">
                {registeredTemplates.length} Saved
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center space-x-1.5">
            {/* Exit Esc */}
            <button
              type="button"
              onClick={onClose}
              className="h-7 px-3 rounded-md border border-slate-300 bg-white hover:bg-slate-100 text-slate-700 font-medium transition flex items-center space-x-1 shadow-xs cursor-pointer text-xs"
            >
              <FiX className="w-3 h-3 text-slate-500" />
              <span>Exit Dialog</span>
              <span className="text-[8px] bg-slate-100 px-1 py-0.2 rounded text-slate-600 border border-slate-300 font-semibold">
                Esc
              </span>
            </button>

            {/* Sample SMS Button */}
            <button
              type="button"
              onClick={handleSendTestSms}
              className="h-7 px-3 rounded-md bg-blue-50 hover:bg-blue-100 border border-blue-300 text-blue-700 font-semibold transition flex items-center space-x-1 shadow-xs cursor-pointer text-xs"
            >
              <FiSend className="w-3 h-3 text-blue-600" />
              <span>Send Sample</span>
            </button>

            {/* Prominent Gold Save Button */}
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="h-7 px-3.5 rounded-md bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-600 hover:to-amber-500 text-slate-950 font-bold tracking-wide shadow-xs transition flex items-center space-x-1 border border-amber-400 cursor-pointer disabled:opacity-50 text-xs"
            >
              <FiSave className="w-3.5 h-3.5 text-slate-950" />
              <span>{isSaving ? "Saving..." : "Save Configuration"}</span>
              <span className="text-[9px] bg-amber-600/30 text-slate-950 px-1 rounded ml-0.5 font-bold">
                F2
              </span>
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default SmsSettings;
